import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { SuggestedPath, SuggestedPathStep } from 'app/shared/models/suggested-path.model';

/**
 * An agent's recommendation for an anomaly: the root cause, then the fix steps.
 *
 * The steps unfold one at a time rather than arriving as a wall of text — a reviewer scanning a
 * tray of anomalies reads the cause first and asks for the next step only if they are acting on it.
 *
 * The single shared implementation for both surfaces: the Alerts tray takes the default, and DEO
 * replies pass `expandAll` because a chat answer is read top to bottom in one go.
 *
 * @example
 * ```html
 * <gains-suggested-path [path]="alert.suggestedPath"></gains-suggested-path>
 * <gains-suggested-path [path]="reply.suggestedPath" [expandAll]="true"></gains-suggested-path>
 * ```
 */
@Component({
  selector: 'gains-suggested-path',
  standalone: false,
  templateUrl: './gains-suggested-path.component.html',
  styleUrl: './gains-suggested-path.component.scss'
})
export class GainsSuggestedPathComponent implements OnChanges {
  /** The recommendation to render. */
  @Input() path?: SuggestedPath;

  /** Section label above the steps. */
  @Input() heading = 'Suggested path';

  /** Reveals every step at once, for surfaces that are read in one pass. */
  @Input() expandAll = false;

  /** How many steps the reviewer has asked for so far. */
  private visibleStepCount = 1;

  /** A new anomaly starts its path from the first step again. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['path']) {
      this.visibleStepCount = 1;
    }
  }

  get hasPath(): boolean {
    return !!this.path?.rootCause || this.steps.length > 0;
  }

  get steps(): SuggestedPathStep[] {
    return this.path?.steps ?? [];
  }

  /** The steps on screen: all of them when expanded, otherwise as far as the reviewer has gone. */
  get visibleSteps(): SuggestedPathStep[] {
    return this.expandAll ? this.steps : this.steps.slice(0, this.visibleStepCount);
  }

  /** Whether there is another step to reveal. */
  get hasMoreSteps(): boolean {
    return !this.expandAll && this.visibleStepCount < this.steps.length;
  }

  /** Label carries the position so the control says what it will do. */
  get nextStepLabel(): string {
    return `Next step (${this.visibleStepCount + 1} of ${this.steps.length})`;
  }

  revealNextStep(): void {
    if (this.hasMoreSteps) {
      this.visibleStepCount += 1;
    }
  }

  protected trackByOrder(index: number, step: SuggestedPathStep): number {
    return step.order;
  }
}
